/**
 * Hangul -> Revised Romanization (국어의 로마자 표기법, 2000).
 *
 * The Korean deck accepts an answer typed in Hangul or in Latin letters, so
 * a learner without a Korean keyboard is not locked out. Two spellings are
 * produced for every word and both are accepted:
 *
 *   - `romanize`: what the standard prescribes, which transcribes the
 *     *pronunciation* — 합니다 is "hamnida", 같이 is "gachi", 좋다 is "jota".
 *     Sound changes covered: final-consonant neutralisation, carry-over to a
 *     vowel-initial syllable, nasalisation, ㄹ liquidisation, palatalisation
 *     of ㄷ/ㅌ before 이, and aspiration around ㅎ.
 *   - `transliterate`: letter for letter, no sound changes — "hapnida". This
 *     is what a beginner reading the Hangul off the card will type.
 *
 * Tensification is not written in either, as the standard says. Hyphens are
 * never emitted; `norm()` would fold them into spaces anyway.
 */

const S_BASE = 0xac00;
const S_END = 0xd7a3;
const V_COUNT = 21;
const T_COUNT = 28;

// Jamo names, indexed the way Unicode indexes them inside a syllable.
const INITIALS = ["ㄱ", "ㄲ", "ㄴ", "ㄷ", "ㄸ", "ㄹ", "ㅁ", "ㅂ", "ㅃ", "ㅅ", "ㅆ", "ㅇ", "ㅈ", "ㅉ", "ㅊ", "ㅋ", "ㅌ", "ㅍ", "ㅎ"] as const;
const VOWELS = ["a", "ae", "ya", "yae", "eo", "e", "yeo", "ye", "o", "wa", "wae", "oe", "yo", "u", "wo", "we", "wi", "yu", "eu", "ui", "i"] as const;
const FINALS = ["", "ㄱ", "ㄲ", "ㄳ", "ㄴ", "ㄵ", "ㄶ", "ㄷ", "ㄹ", "ㄺ", "ㄻ", "ㄼ", "ㄽ", "ㄾ", "ㄿ", "ㅀ", "ㅁ", "ㅂ", "ㅄ", "ㅅ", "ㅆ", "ㅇ", "ㅈ", "ㅊ", "ㅋ", "ㅌ", "ㅍ", "ㅎ"] as const;

type Jamo = string;

/** Romanisation of a consonant in initial (onset) position. */
const ONSET: Record<Jamo, string> = {
  ㄱ: "g", ㄲ: "kk", ㄴ: "n", ㄷ: "d", ㄸ: "tt", ㄹ: "r", ㅁ: "m", ㅂ: "b", ㅃ: "pp",
  ㅅ: "s", ㅆ: "ss", ㅇ: "", ㅈ: "j", ㅉ: "jj", ㅊ: "ch", ㅋ: "k", ㅌ: "t", ㅍ: "p", ㅎ: "h",
};

/** Romanisation of a consonant in final (coda) position, once neutralised. */
const CODA: Record<Jamo, string> = { ㄱ: "k", ㄴ: "n", ㄷ: "t", ㄹ: "l", ㅁ: "m", ㅂ: "p", ㅇ: "ng" };

/** The seven sounds a syllable can end in, and which finals reduce to them. */
const NEUTRAL: Record<Jamo, Jamo> = {
  ㄱ: "ㄱ", ㄲ: "ㄱ", ㅋ: "ㄱ",
  ㄴ: "ㄴ",
  ㄷ: "ㄷ", ㅅ: "ㄷ", ㅆ: "ㄷ", ㅈ: "ㄷ", ㅊ: "ㄷ", ㅌ: "ㄷ", ㅎ: "ㄷ",
  ㄹ: "ㄹ",
  ㅁ: "ㅁ",
  ㅂ: "ㅂ", ㅍ: "ㅂ",
  ㅇ: "ㅇ",
};

/**
 * Compound finals. `alone` is the one sound heard before a consonant or at the
 * end of a word (닭 -> dak); `split` is what happens before a vowel — the
 * first letter stays as the coda and the second becomes the next onset
 * (읽어 -> ilgeo, 삶이 -> salmi).
 */
const CLUSTER: Record<Jamo, { alone: Jamo; split: [Jamo, Jamo] }> = {
  ㄳ: { alone: "ㄱ", split: ["ㄱ", "ㅅ"] },
  ㄵ: { alone: "ㄴ", split: ["ㄴ", "ㅈ"] },
  ㄶ: { alone: "ㄴ", split: ["ㄴ", "ㅎ"] },
  ㄺ: { alone: "ㄱ", split: ["ㄹ", "ㄱ"] },
  ㄻ: { alone: "ㅁ", split: ["ㄹ", "ㅁ"] },
  ㄼ: { alone: "ㄹ", split: ["ㄹ", "ㅂ"] },
  ㄽ: { alone: "ㄹ", split: ["ㄹ", "ㅅ"] },
  ㄾ: { alone: "ㄹ", split: ["ㄹ", "ㅌ"] },
  ㄿ: { alone: "ㅂ", split: ["ㄹ", "ㅍ"] },
  ㅀ: { alone: "ㄹ", split: ["ㄹ", "ㅎ"] },
  ㅄ: { alone: "ㅂ", split: ["ㅂ", "ㅅ"] },
};

/** Finals that end in ㅎ, which aspirates a following ㄱ/ㄷ/ㅈ (많다 -> manta). */
const H_FINAL: Record<Jamo, Jamo> = { ㅎ: "", ㄶ: "ㄴ", ㅀ: "ㄹ" };

const ASPIRATE: Record<Jamo, Jamo> = { ㄱ: "ㅋ", ㄷ: "ㅌ", ㅂ: "ㅍ", ㅈ: "ㅊ" };

type Syllable = { onset: Jamo; vowel: string; coda: Jamo };

function decompose(ch: string): Syllable | null {
  const code = ch.charCodeAt(0);
  if (code < S_BASE || code > S_END) return null;
  const idx = code - S_BASE;
  return {
    onset: INITIALS[Math.floor(idx / (V_COUNT * T_COUNT))]!,
    vowel: VOWELS[Math.floor(idx / T_COUNT) % V_COUNT]!,
    coda: FINALS[idx % T_COUNT]!,
  };
}

/** One word (no spaces): the standard, pronunciation-based spelling. */
function romanizeWord(word: string): string {
  const syls = [...word].map(decompose);
  // Anything that is not a syllable (digits, Latin, punctuation) passes through.
  const out: string[] = [];

  // Pass 1: resolve every coda to a single sound, handing a cluster's second
  // consonant (or a plain consonant) to the next syllable when it starts with
  // a vowel — that consonant is then pronounced as an onset, unchanged.
  const onsets: (Jamo | null)[] = syls.map((s) => (s ? s.onset : null));
  const codas: (Jamo | null)[] = syls.map((s) => (s ? s.coda : null));

  for (let i = 0; i < syls.length; i++) {
    const s = syls[i];
    if (!s || !s.coda) continue;
    const next = syls[i + 1];
    const nextIsVowel = next !== null && next !== undefined && next.onset === "ㅇ";

    if (s.coda in CLUSTER) {
      const { alone, split } = CLUSTER[s.coda]!;
      if (nextIsVowel) {
        codas[i] = split[0];
        onsets[i + 1] = split[1] === "ㅎ" ? "ㅇ" : split[1]; // ㅎ before a vowel is silent
      } else {
        codas[i] = alone;
      }
      continue;
    }

    if (nextIsVowel) {
      if (s.coda === "ㅇ") continue; // ㅇ stays a coda: 강아지 -> gangaji
      if (s.coda === "ㅎ") {
        codas[i] = ""; // 좋아 -> joa
        continue;
      }
      // Palatalisation: 같이 -> gachi, 굳이 -> guji.
      if ((s.coda === "ㄷ" || s.coda === "ㅌ") && next!.vowel === "i") {
        codas[i] = "";
        onsets[i + 1] = s.coda === "ㄷ" ? "ㅈ" : "ㅊ";
        continue;
      }
      codas[i] = "";
      onsets[i + 1] = s.coda; // 밥이 -> babi, 옷이 -> osi, 꽃이 -> kkochi
      continue;
    }

    codas[i] = NEUTRAL[s.coda] ?? s.coda;
  }

  // Pass 2: interactions between a coda and the following onset.
  for (let i = 0; i < syls.length - 1; i++) {
    const c = codas[i];
    const o = onsets[i + 1];
    if (!c || !o || syls[i + 1] === null) continue;

    // Aspiration: 좋다 -> jota, 많다 -> manta, 축하 -> chuka, 입학 -> ipak.
    const original = syls[i]!.coda;
    if (original in H_FINAL && o in ASPIRATE) {
      codas[i] = H_FINAL[original]!;
      onsets[i + 1] = ASPIRATE[o]!;
      continue;
    }
    if (o === "ㅎ" && c in ASPIRATE) {
      codas[i] = "";
      onsets[i + 1] = ASPIRATE[c]!;
      continue;
    }

    // ㄹ + ㄴ -> ㄹㄹ: 설날 -> seollal. Checked before nasalisation, which
    // would otherwise claim the ㄴ.
    if (c === "ㄹ" && o === "ㄴ") {
      onsets[i + 1] = "ㄹ";
      continue;
    }

    // Nasalisation: 합니다 -> hamnida, 국물 -> gungmul, 믿는 -> minneun.
    if (o === "ㄴ" || o === "ㅁ") {
      if (c === "ㄱ") codas[i] = "ㅇ";
      else if (c === "ㄷ") codas[i] = "ㄴ";
      else if (c === "ㅂ") codas[i] = "ㅁ";
      continue;
    }

    if (o === "ㄹ") {
      // 음료 -> eumnyo, 정류장 -> jeongnyujang, 국립 -> gungnip, 압력 -> amnyeok.
      if (c === "ㅁ" || c === "ㅇ") onsets[i + 1] = "ㄴ";
      else if (c === "ㄱ") { codas[i] = "ㅇ"; onsets[i + 1] = "ㄴ"; }
      else if (c === "ㅂ") { codas[i] = "ㅁ"; onsets[i + 1] = "ㄴ"; }
      // ㄴ + ㄹ -> ㄹㄹ: 신라 -> silla.
      else if (c === "ㄴ") codas[i] = "ㄹ";
    }
  }

  // Pass 3: spell it out.
  for (let i = 0; i < syls.length; i++) {
    const s = syls[i];
    if (!s) {
      out.push(word[i]!);
      continue;
    }
    const onset = onsets[i]!;
    const coda = codas[i]!;
    // A ㄹ onset right after a ㄹ coda is "l", giving "ll".
    const onsetText = onset === "ㄹ" && codas[i - 1] === "ㄹ" ? "l" : ONSET[onset] ?? "";
    out.push(onsetText + s.vowel + (coda ? CODA[coda] ?? "" : ""));
  }
  return out.join("");
}

/** Letter-for-letter spelling of one word, no sound changes. */
function transliterateWord(word: string): string {
  return [...word]
    .map((ch) => {
      const s = decompose(ch);
      if (!s) return ch;
      let coda = "";
      if (s.coda) {
        const letters = s.coda in CLUSTER ? CLUSTER[s.coda]!.split : [s.coda];
        coda = letters.map((j) => CODA[NEUTRAL[j] ?? j] ?? ONSET[j] ?? "").join("");
      }
      return ONSET[s.onset] + s.vowel + coda;
    })
    .join("");
}

const perWord = (text: string, f: (w: string) => string): string =>
  text.trim().split(/\s+/).map(f).join(" ");

/** Standard Revised Romanization of `text`, word by word. */
export const romanize = (text: string): string => perWord(text, romanizeWord);

/** Naive letter-for-letter romanisation of `text`, word by word. */
export const transliterate = (text: string): string => perWord(text, transliterateWord);

/**
 * Every Latin spelling accepted for a Korean answer: the standard one, the
 * naive one, the "shi" habit for 시 that most beginners bring from English
 * transcriptions, and each of those without spaces. Deduplicated, standard
 * spelling first so it can be shown as the hint.
 */
export function romanizations(text: string): string[] {
  const rr = romanize(text);
  const forms = [rr, transliterate(text)];
  for (const f of forms.slice()) if (f.includes("si")) forms.push(f.replace(/si/g, "shi"));
  for (const f of forms.slice()) if (f.includes(" ")) forms.push(f.replace(/ /g, ""));
  return [...new Set(forms)];
}
